import React, { useState } from "react";

import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";
import "pages/AdminPage.scss";

interface SellerInquiry {
  id: string;
  subject: string;
  seller: string;
  category: "정산" | "상품" | "계정" | "기타";
  status: "pending" | "answered" | "closed";
  createdAt: string;
}

const MOCK: SellerInquiry[] = [
  {
    id: "1",
    subject: "정산 금액이 맞지 않습니다",
    seller: "플라워하우스",
    category: "정산",
    status: "pending",
    createdAt: "2024-03-05 10:15",
  },
  {
    id: "2",
    subject: "상품 등록 시 오류 발생",
    seller: "에버그린",
    category: "상품",
    status: "answered",
    createdAt: "2024-03-04 14:30",
  },
  {
    id: "3",
    subject: "비밀번호 초기화 요청",
    seller: "골든벨",
    category: "계정",
    status: "answered",
    createdAt: "2024-03-03 09:00",
  },
  {
    id: "4",
    subject: "수수료율 변경 요청",
    seller: "화이트라인",
    category: "정산",
    status: "pending",
    createdAt: "2024-03-05 16:45",
  },
  {
    id: "5",
    subject: "배송 정책 관련 문의",
    seller: "블루문",
    category: "기타",
    status: "closed",
    createdAt: "2024-03-02 11:20",
  },
];

const STATUS_BADGE: Record<string, "pending" | "active" | "draft"> = {
  pending: "pending",
  answered: "active",
  closed: "draft",
};
const STATUS_LABEL: Record<string, string> = {
  pending: "답변 대기",
  answered: "답변 완료",
  closed: "완료",
};

const TABS: TabItem[] = [
  { key: "all", label: "전체", count: 5 },
  { key: "pending", label: "답변 대기", count: 2 },
  { key: "answered", label: "답변 완료", count: 2 },
  { key: "closed", label: "완료", count: 1 },
];

const SellerCS: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState<SellerInquiry | null>(null);
  const [answer, setAnswer] = useState("");

  const filtered = MOCK.filter((i) => activeTab === "all" || i.status === activeTab);

  const columns: Column<SellerInquiry>[] = [
    { key: "subject", title: "제목", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: "seller", title: "셀러", width: "120px" },
    {
      key: "category",
      title: "유형",
      width: "90px",
      render: (v) => <Badge variant="info">{v}</Badge>,
    },
    { key: "createdAt", title: "문의일", width: "140px" },
    {
      key: "status",
      title: "상태",
      width: "100px",
      render: (v) => <Badge variant={STATUS_BADGE[v]}>{STATUS_LABEL[v]}</Badge>,
    },
    {
      key: "actions",
      title: "관리",
      width: "80px",
      render: (_, row) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setSelected(row);
            setAnswer("");
          }}
        >
          상세
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="셀러 CS" description="셀러 문의를 조회하고 답변합니다." />
      <div className="admin-page__card">
        <Tabs
          items={TABS}
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            setCurrentPage(1);
          }}
        />
        <div className="admin-page__filter">
          <Input placeholder="제목, 셀러명 검색" style={{ width: 260 }} prefix="🔍" />
        </div>
        <Table
          columns={columns}
          data={filtered}
          rowKey="id"
          onRowClick={(row) => {
            setSelected(row);
            setAnswer("");
          }}
        />
        <Pagination current={currentPage} total={filtered.length} onChange={setCurrentPage} />
      </div>

      <Modal
        open={!!selected}
        title="셀러 문의 상세"
        onClose={() => setSelected(null)}
        width="540px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSelected(null)}>
              닫기
            </Button>
            {selected?.status === "pending" && (
              <Button variant="primary" onClick={() => setSelected(null)}>
                답변 등록
              </Button>
            )}
          </>
        }
      >
        {selected && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ background: "#F8F8F8", borderRadius: 8, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <Badge variant="info">{selected.category}</Badge>
                <Badge variant={STATUS_BADGE[selected.status]}>{STATUS_LABEL[selected.status]}</Badge>
              </div>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{selected.subject}</div>
              <div style={{ fontSize: 12, color: "#71717A" }}>
                셀러: {selected.seller} · {selected.createdAt}
              </div>
            </div>
            <div
              style={{
                background: "#F0F9FF",
                border: "1px solid #BAE6FD",
                borderRadius: 8,
                padding: 16,
                fontSize: 13,
                lineHeight: 1.6,
                color: "#0C4A6E",
              }}
            >
              문의 내용이 여기에 표시됩니다. 실제 서비스에서는 API를 통해 상세 내용을 불러옵니다.
            </div>
            {selected.status === "pending" && (
              <div>
                <label style={{ display: "block", fontWeight: 600, fontSize: 14, marginBottom: 8 }}>답변 작성</label>
                <textarea
                  rows={5}
                  placeholder="셀러에게 보낼 답변을 작성해주세요."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  style={{
                    width: "100%",
                    padding: 12,
                    border: "1px solid #E4E4E7",
                    borderRadius: 8,
                    fontSize: 13,
                    resize: "vertical",
                    outline: "none",
                    lineHeight: 1.6,
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SellerCS;
